import React from 'react';
import { SEOHead } from '../components/SEOHead';
import {
    PrivacyContainer,
    PrivacyContent,
    PageTitle,
    LastUpdated,
    Section,
    SectionTitle,
    Paragraph,
    List,
    ContactInfo,
    BackLink
} from './PrivacyPolicyPage.styled';

function PrivacyPolicyPage() {
    return (
        <PrivacyContainer>
            <SEOHead
                title="Política de Privacidad - Duo a Color"
                description="Política de privacidad de Duo a Color (duoacolor.com). Información sobre protección de datos y tratamiento de información personal."
                path="/politica-privacidad"
            />
            <PrivacyContent>
                <BackLink href="/">Volver al inicio</BackLink>

                <PageTitle>Política de Privacidad</PageTitle>
                <LastUpdated>Última actualización: Enero 2026</LastUpdated>

                <Section>
                    <SectionTitle>Responsable del Tratamiento</SectionTitle>
                    <Paragraph>
                        <strong>Duo a Color</strong> es el responsable del tratamiento de los datos personales
                        que nos facilites a través de nuestros formularios de contacto y reservas.
                    </Paragraph>
                    <ContactInfo>
                        <p><strong>Email de contacto:</strong> <a href="mailto:contacto@duoacolor.com">contacto@duoacolor.com</a></p>
                        <p><strong>WhatsApp:</strong> <a href="https://wa.me/+34722177469">+34 722 177 469</a></p>
                    </ContactInfo>
                </Section>

                <Section>
                    <SectionTitle>Datos que Recopilamos</SectionTitle>
                    <Paragraph>
                        A través de nuestros formularios, recopilamos los siguientes datos personales:
                    </Paragraph>
                    <List>
                        <li><strong>Formulario de contacto:</strong> Nombre, correo electrónico y mensaje</li>
                        <li><strong>Formulario de reservas:</strong> Nombre, correo electrónico y teléfono</li>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>Finalidad del Tratamiento</SectionTitle>
                    <Paragraph>
                        Tus datos personales serán tratados con las siguientes finalidades:
                    </Paragraph>
                    <List>
                        <li>Gestionar y responder a tus solicitudes de contacto</li>
                        <li>Procesar y gestionar tus reservas de productos</li>
                        <li>Comunicarnos contigo para confirmar detalles de tu pedido</li>
                        <li>Con tu consentimiento expreso, compartir parte de tu historia como inspiración para nuestra comunidad (de forma anónima o con tu permiso explícito)</li>
                    </List>
                </Section>

                <Section>
                    <SectionTitle>Base Legal del Tratamiento</SectionTitle>
                    <Paragraph>
                        La base legal para el tratamiento de tus datos es el <strong>consentimiento del interesado</strong> (Artículo 6.1.a del RGPD),
                        que nos otorgas al marcar la casilla de aceptación de esta política de privacidad en nuestros formularios.
                    </Paragraph>
                    <Paragraph>
                        El consentimiento para compartir tu historia es independiente y opcional, y puedes otorgarlo o revocarlo
                        en cualquier momento sin que afecte al resto del tratamiento de tus datos.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Destinatarios de los Datos</SectionTitle>
                    <Paragraph>
                        <strong>No cedemos tus datos personales a terceros</strong>, salvo obligación legal.
                    </Paragraph>
                    <Paragraph>
                        Utilizamos <strong>EmailJS</strong> como servicio para el envío de correos electrónicos.
                        Este servicio actúa como encargado del tratamiento y solo procesa tus datos para
                        el envío de comunicaciones en nuestro nombre.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Conservación de los Datos</SectionTitle>
                    <Paragraph>
                        Conservaremos tus datos personales durante el tiempo necesario para gestionar tu solicitud
                        y durante el plazo legalmente establecido para atender posibles responsabilidades derivadas del tratamiento.
                    </Paragraph>
                    <Paragraph>
                        Una vez finalizada la relación, tus datos serán bloqueados durante el plazo de prescripción
                        de las acciones legales que pudieran derivarse, y posteriormente serán eliminados.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Tus Derechos</SectionTitle>
                    <Paragraph>
                        Como titular de los datos, tienes derecho a:
                    </Paragraph>
                    <List>
                        <li><strong>Acceso:</strong> Conocer qué datos personales tenemos sobre ti</li>
                        <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
                        <li><strong>Supresión:</strong> Solicitar la eliminación de tus datos</li>
                        <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos</li>
                        <li><strong>Limitación:</strong> Solicitar la limitación del tratamiento</li>
                        <li><strong>Portabilidad:</strong> Recibir tus datos en un formato estructurado</li>
                        <li><strong>Revocación del consentimiento:</strong> Retirar tu consentimiento en cualquier momento</li>
                    </List>
                    <Paragraph>
                        Para ejercer cualquiera de estos derechos, puedes escribirnos a{' '}
                        <a href="mailto:contacto@duoacolor.com">contacto@duoacolor.com</a> indicando
                        el derecho que deseas ejercer y adjuntando copia de tu documento de identidad.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Derecho a Reclamar</SectionTitle>
                    <Paragraph>
                        Si consideras que el tratamiento de tus datos personales no es adecuado,
                        tienes derecho a presentar una reclamación ante la{' '}
                        <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">
                            Agencia Española de Protección de Datos (AEPD)
                        </a>.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Seguridad de los Datos</SectionTitle>
                    <Paragraph>
                        Hemos implementado medidas técnicas y organizativas apropiadas para proteger
                        tus datos personales contra el tratamiento no autorizado o ilícito, y contra
                        su pérdida, destrucción o daño accidental.
                    </Paragraph>
                </Section>

                <Section>
                    <SectionTitle>Actualizaciones de esta Política</SectionTitle>
                    <Paragraph>
                        Podemos actualizar esta política de privacidad periódicamente.
                        Te notificaremos cualquier cambio significativo publicando la nueva política
                        en esta página con una nueva fecha de actualización.
                    </Paragraph>
                </Section>
            </PrivacyContent>
        </PrivacyContainer>
    );
}

export default PrivacyPolicyPage;
